// Storage on src/ui (DESIGN rev 2.1 §3.1): the cold archive (Glacier Instant). Sibling of Raw footage, not part of it:
// files here are inert until someone promotes one into Live Raw footage.
//
// Layout: the client rollup is a Grid; inside a project, the folder tree is a left column in the page (§3.1 puts it
// "in the expanded sidebar position"; the shell has no slot for it yet), with the breadcrumb bar, a medium-density
// Grid of folders and files and the Expanded-row panel beside it. Uploads go to the global Upload sheet.
//
// Same calls as before, same params: GET /api/clients (internal), GET BASE/summary, GET BASE/folders?clientId,
// GET BASE/folders/:id|root/children, GET BASE/files (clientId, page, pageSize 50, folderId all|<id>, search),
// GET BASE/files/:id (deep link, promote polling), POST/PATCH/DELETE BASE/folders, POST BASE/folders/resolve (dry run,
// then real), the multipart upload routes + POST BASE/upload/confirm, PATCH/DELETE BASE/files/:id,
// POST BASE/files/:id/promote, GET BASE/files/deleted, POST BASE/files/:id/restore. Frame.io and the download modal
// keep their own calls (see their files).

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArchiveRestore, ArrowUp, ArrowRightLeft, ChevronDown, ChevronRight, Download, Folder, FolderInput, FolderPlus, FolderUp,
  LayoutGrid, List, MoreHorizontal, Pencil, Search, Trash2, Upload, X,
} from 'lucide-react';
import api from '../lib/api';
import {
  entriesFromPicker, entriesFromDataTransfer, dropContainsDirectory,
  buildPreflight, formatBytes,
} from '../lib/folderUpload';
import { useAuth } from '../context/AuthContext';
import FrameioMigrationPanel from '../components/FrameioMigrationPanel';
import StorageDownloadModal, { StorageDownloadDock } from '../components/StorageDownloadModal';
import { dismissFinishedDownloads } from '../lib/storageDownload';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import Checkbox from '../ui/Checkbox';
import EmptyState from '../ui/EmptyState';
import ErrorState from '../ui/ErrorState';
import ExpandedRowPanel from '../ui/ExpandedRowPanel';
import Grid from '../ui/Grid';
import Icon from '../ui/Icon';
import Input from '../ui/Input';
import Menu from '../ui/Menu';
import Modal from '../ui/Modal';
import Popover from '../ui/Popover';
import Select from '../ui/Select';
import Skeleton from '../ui/Skeleton';
import Table from '../ui/Table';
import Tag from '../ui/Tag';
import Textarea from '../ui/Textarea';
import { useConfirm } from '../ui/Confirm';
import { useToast } from '../ui/Toast';
import { usePageHeader } from '../shell/pageHeader';
import { clean } from '../shell/copy';
import { enqueueUpload, useUploadDone } from '../shell/uploads';
import {
  ACCEPT_EXTS, ACCEPT_RE, BASE, PAGE_SIZE, UPLOAD_ENDPOINTS, deleteBody, disambiguate, errMsg, flattenTree, fmtDate, indentOptions,
  mayDeleteFile, plural,
} from './storage/archive';
import PreflightModal from './storage/PreflightModal';
import PortalStorage from './storage/PortalStorage';

const WRITE_ROLES = ['Editor', 'Producer', 'Post Lead', 'Post_Lead', 'Power User', 'Power_User', 'Admin'];
const ADMIN_ROLES = ['Admin', 'Power User', 'Power_User'];

const SOURCE_LABELS = {
  client_upload:       'Project upload',
  internal_upload:     'Internal upload',
  frameio_migration:   'Frame.io migration',
  raw_footage_archive: 'Archived from Raw footage',
};
// Files that got here by archiving a Raw footage clip: the clip is still in Raw footage with its transcript and match
// history, and Promote is what brings the file back.
const FOOTAGE_ARCHIVE_SOURCE = 'raw_footage_archive';

// ── Helpers ───────────────────────────────────────────────────────────────────

// The API returns every byte count as a STRING (sums of bigint columns); this is only for arithmetic on the page.
function bytesNum(v) { const n = Number(v || 0); return Number.isFinite(n) ? n : 0; }
function extOf(name) { const m = /\.([A-Za-z0-9]{1,6})$/.exec(name || ''); return m ? m[1].toUpperCase() : 'FILE'; }
const sourceLabel = s => SOURCE_LABELS[s] || clean(s) || '';

function findNode(nodes, id) {
  for (const n of nodes) {
    if (n.id === id) return n;
    const hit = findNode(n.children || [], id);
    if (hit) return hit;
  }
  return null;
}

// Status of an archived file: a condition Badge where something is happening or went wrong, a Tag at rest.
function FileStatus({ file, link = true }) {
  if (file.promoteStatus === 'promoted') {
    const badge = <Badge tone="ok">Promoted</Badge>;
    return link && file.promotedFootageId ? (
      <Link className="ui-plainlink" to={`/footage?footage=${file.promotedFootageId}`} onClick={e => e.stopPropagation()}
        aria-label={`Promoted. Open ${clean(file.fileName)} in Raw footage`}>{badge}</Link>
    ) : badge;
  }
  if (file.promoteStatus === 'promoting') return <Badge tone="info">Promoting</Badge>;
  if (file.promoteStatus === 'failed') return <span title={clean(file.promoteError) || undefined}><Badge tone="danger">Promote failed</Badge></span>;
  if (file.uploadStatus === 'failed') return <Badge tone="danger">Upload failed</Badge>;
  if (file.uploadStatus === 'pending') return <Badge tone="info">Uploading</Badge>;
  return <Tag>Archived</Tag>;
}
function FootageArchiveTag({ file }) {
  if (file?.source !== FOOTAGE_ARCHIVE_SOURCE) return null;
  return <Tag title="Archived from a Raw footage clip. Promote copies it back.">From Raw footage</Tag>;
}

// ── Page ──────────────────────────────────────────────────────────────────────

// External client accounts get the Portal page (DESIGN rev 2.1 §3.3, HD-9); staff keep this Console page as it was.
export default function Storage() {
  const { user } = useAuth();
  return user?.isExternal ? <PortalStorage /> : <ConsoleStorage />;
}

function ConsoleStorage() {
  const { user } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const isExternal = !!user?.isExternal;
  const roles = user?.roles || [];

  const canWrite   = isExternal ? !!user?.assignedClientIds?.length : roles.some(r => WRITE_ROLES.includes(r));
  const canPromote = !isExternal && roles.some(r => WRITE_ROLES.includes(r));
  const isAdmin    = !isExternal && roles.some(r => ADMIN_ROLES.includes(r));
  // Delete is Admin OR the file's own uploader; see mayDeleteFile and the matching gate in the API.
  const deletePerm = useMemo(() => ({ isExternal, isAdmin, userId: user?.id || null }), [isExternal, isAdmin, user?.id]);
  // The Frame.io migration is Admin-only and internal-only (matches routes/frameio.js).
  const canMigrate = isAdmin;

  // ── Where you are lives in the URL ──────────────────────────────────────────
  // clientId and folderId are read FROM the query string (one source of truth; a pasted URL is the ordinary case).
  // folderId: 'all' | 'unfiled' | <folder id>. 'root' is read as the project root (files held directly there), which
  // is what 'unfiled' lists.
  const [searchParams, setSearchParams] = useSearchParams();
  const clientId = searchParams.get('clientId') || '';
  const rawFolder = searchParams.get('folderId') || 'all';
  const selected = rawFolder === 'root' ? 'unfiled' : rawFolder;

  // A move the USER makes pushes a history entry; a move the PAGE makes (landing a single-client user, resolving a
  // ?fileId= deep link, leaving a folder just deleted) replaces it.
  const go = useCallback((next, { replace = false } = {}) => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      p.delete('fileId');   // consumed on arrival, never carried forward
      if ('client' in next) { if (next.client) p.set('clientId', next.client); else p.delete('clientId'); }
      if ('folder' in next) { if (next.folder) p.set('folderId', next.folder); else p.delete('folderId'); }
      return p;
    }, { replace });
  }, [setSearchParams]);
  const openFolder = useCallback((id, opts) => go({ folder: id }, opts), [go]);
  const pickClient = useCallback(id => go({ client: id, folder: 'all' }), [go]);
  const toRollup   = useCallback(() => go({ client: null, folder: null }), [go]);

  const [clients, setClients]   = useState([]);
  const [tree, setTree]         = useState([]);
  const [counts, setCounts]     = useState({ unfiledCount: 0, totalCount: 0 });
  const [expanded, setExpanded] = useState(() => new Set());
  const [files, setFiles]       = useState([]);
  const [filesState, setFilesState] = useState('data');
  const [filesError, setFilesError] = useState(null);
  const [retention, setRetention] = useState(null);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [search, setSearch]     = useState('');
  const [detail, setDetail]     = useState(null);
  const [summary, setSummary]   = useState(null);
  const [summaryError, setSummaryError] = useState(false);
  const [browse, setBrowse]     = useState(null);   // { folder, children, assets }
  const [selectedFiles, setSelectedFiles] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting]   = useState(false);
  const [migrationOpen, setMigrationOpen] = useState(false);
  const [deletedOpen, setDeletedOpen]     = useState(false);
  const [folderForm, setFolderForm]       = useState(null);   // { mode: 'new'|'rename', node?, name }
  const [movingFolder, setMovingFolder]   = useState(null);
  const [preflight, setPreflight]         = useState(null);   // folder upload: { walking, count } | { summary, dry, error, dest }
  const [dropOver, setDropOver]           = useState(false);
  const fileInput = useRef(null);
  const folderInput = useRef(null);

  // List vs gallery: a preference, not a location, so it lives in localStorage (same key as before).
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('storageViewMode') === 'grid' ? 'grid' : 'list'; } catch { return 'list'; }
  });
  useEffect(() => { try { localStorage.setItem('storageViewMode', viewMode); } catch { /* private mode */ } }, [viewMode]);

  // Bulk selection is pruned to the visible files on every fetch, so a hidden row can never be mass-deleted.
  useEffect(() => {
    setSelectedFiles(prev => {
      if (prev.size === 0) return prev;
      const visible = new Set(files.map(f => f.id));
      const next = new Set([...prev].filter(id => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [files]);

  // Deep link from a Live footage row: open that file's panel in its client and folder, replacing the ?fileId= URL.
  useEffect(() => {
    const fileId = searchParams.get('fileId');
    if (!fileId) return;
    api.get(`${BASE}/files/${fileId}`)
      .then(({ data }) => {
        setDetail(data);
        go({ client: data.clientId, folder: data.folderId || 'unfiled' }, { replace: true });
      })
      .catch(() => {
        toast('That archived file is no longer available.', { tone: 'warn' });
        go({}, { replace: true });
      });
  }, [searchParams, go, toast]);

  // Internal users pick a client; external users are pinned to their own.
  useEffect(() => {
    if (isExternal) return;
    api.get('/api/clients')
      .then(({ data }) => setClients(Array.isArray(data) ? data : (data.rows || [])))
      .catch(() => setClients([]));
  }, [isExternal]);

  // The client rollup, loaded for every user: it is also how a single-client user is landed in their own tree.
  const loadSummary = useCallback(() => {
    setSummaryError(false);
    api.get(`${BASE}/summary`)
      .then(({ data }) => setSummary(data))
      .catch(() => setSummaryError(true));
  }, []);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  useEffect(() => {
    if (!summary || clientId || searchParams.get('fileId')) return;
    if ((summary.clients || []).length === 1) go({ client: summary.clients[0].clientId, folder: 'all' }, { replace: true });
  }, [summary, clientId, searchParams, go]);

  // A pasted URL arrives with the tree expanded to the folder it names (the breadcrumb IS that ancestor chain).
  useEffect(() => {
    const crumbs = browse?.folder?.breadcrumb || [];
    if (!crumbs.length) return;
    setExpanded(prev => {
      const next = new Set(prev);
      let changed = false;
      for (const c of crumbs) if (!next.has(c.id)) { next.add(c.id); changed = true; }
      return changed ? next : prev;
    });
  }, [browse]);

  const atRoot = selected === 'all' || selected === 'unfiled';

  const loadBrowse = useCallback(async () => {
    if (!clientId) { setBrowse(null); return; }
    const root = selected === 'all' || selected === 'unfiled';
    try {
      const { data } = await api.get(`${BASE}/folders/${root ? 'root' : selected}/children`, root ? { params: { clientId } } : undefined);
      setBrowse(data);
    } catch {
      setBrowse(null);   // a folder just deleted or out of scope: the folder rows go; the file list reports its own error
    }
  }, [clientId, selected]);
  useEffect(() => { loadBrowse(); }, [loadBrowse]);

  const loadTree = useCallback(async () => {
    if (!clientId) { setTree([]); setCounts({ unfiledCount: 0, totalCount: 0 }); return; }
    try {
      const { data } = await api.get(`${BASE}/folders`, { params: { clientId } });
      setTree(data.folders || []);
      setCounts({ unfiledCount: data.unfiledCount || 0, totalCount: data.totalCount || 0 });
    } catch (err) { toast(errMsg(err, 'The folders did not load. Refresh the page to try again.'), { tone: 'danger' }); }
  }, [clientId, toast]);

  const loadFiles = useCallback(async () => {
    if (!clientId) { setFiles([]); setTotal(0); return; }
    setFilesState('loading');
    try {
      const params = { clientId, page, pageSize: PAGE_SIZE };
      if (selected === 'all')          params.folderId = 'all';
      else if (selected !== 'unfiled') params.folderId = selected;
      if (search.trim())               params.search = search.trim();
      const { data } = await api.get(`${BASE}/files`, { params });
      setFiles(data.rows || []);
      setTotal(data.total || 0);
      if (data.retention) setRetention(data.retention);
      setFilesState('data');
    } catch (err) {
      setFilesError(errMsg(err, 'Check your connection, then try again.'));
      setFiles([]);
      setFilesState('error');
    }
  }, [clientId, selected, search, page]);

  useEffect(() => { loadTree(); }, [loadTree]);
  useEffect(() => {
    const t = setTimeout(loadFiles, search ? 250 : 0);   // debounce typing
    return () => clearTimeout(t);
  }, [loadFiles, search]);
  useEffect(() => { setPage(1); }, [selected, search, clientId]);

  const refresh = useCallback(() => { loadTree(); loadFiles(); loadBrowse(); }, [loadTree, loadFiles, loadBrowse]);

  // Every Storage upload that finishes refreshes the tree, the list and the folder rows (as each file's confirm did).
  useUploadDone(item => { if (item.meta?.kind === 'storage') refresh(); });

  const toggle = useCallback(id => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  }), []);

  // ── Folder actions ──────────────────────────────────────────────────────────

  async function submitFolderForm() {
    const name = (folderForm?.name || '').trim();
    if (!name) return;
    if (folderForm.mode === 'rename') {
      if (name === folderForm.node.name) { setFolderForm(null); return; }
      try {
        await api.patch(`${BASE}/folders/${folderForm.node.id}`, { name });
        setFolderForm(null);
        refresh();
      } catch (err) { toast(errMsg(err, 'The folder was not renamed. Try again.'), { tone: 'danger' }); }
      return;
    }
    const parentId = atRoot ? null : selected;
    try {
      await api.post(`${BASE}/folders`, { clientId, parentId, name });
      if (parentId) setExpanded(prev => new Set(prev).add(parentId));
      setFolderForm(null);
      loadTree();
    } catch (err) { toast(errMsg(err, 'The folder was not created. Try again.'), { tone: 'danger' }); }
  }

  async function deleteFolder(node) {
    const ok = await confirm({
      title: `Delete the folder ${clean(node.name)}?`,
      body: 'Only an empty folder can be deleted. If it still holds files or folders, nothing is removed.',
      confirmLabel: 'Delete folder',
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`${BASE}/folders/${node.id}`);
      if (selected === node.id) openFolder('all', { replace: true });
      refresh();
      toast(`Folder ${clean(node.name)} deleted.`);
    } catch (err) {
      // The 409 body carries the blocking counts.
      toast(errMsg(err, 'The folder was not deleted. Try again.'), { tone: 'danger' });
    }
  }

  // ── Uploads ─────────────────────────────────────────────────────────────────

  const selectedNode = !atRoot ? findNode(tree, selected) : null;
  // A file upload needs a destination folder, so it is disabled at "All files"; a folder upload brings its own
  // structure and runs at the project root from there.
  const canUploadFiles  = canWrite && !!clientId && selected !== 'all';
  const canUploadFolder = canWrite && !!clientId;
  const fileTarget   = selected === 'unfiled' ? 'Unfiled' : clean(selectedNode?.path || selectedNode?.name) || 'this folder';
  const folderTarget = selected === 'all' ? 'the project root' : fileTarget;
  const destFolderId = atRoot ? null : selected;

  const queueFile = useCallback((file, dest) => {
    enqueueUpload({
      file,
      endpoints: UPLOAD_ENDPOINTS,
      startBody: { clientId, folderId: dest || null },
      idFieldName: 'archiveId',
      confirm: async archiveId => (await api.post(`${BASE}/upload/confirm`, { archiveId })).data,
      meta: { kind: 'storage', clientId, folderId: dest || null },
    });
  }, [clientId]);

  function handleFiles(list) {
    const all = [...(list || [])];
    // External users face the closed allowlist; internal users are gated server-side by a denylist.
    const accepted = isExternal ? all.filter(f => ACCEPT_RE.test(f.name)) : all;
    if (!accepted.length) {
      if (all.length) toast('None of those file types can be uploaded to your archive.', { tone: 'warn' });
      return;
    }
    accepted.forEach(f => queueFile(f, destFolderId));
    toast(`${plural(accepted.length, 'file')} uploading to ${fileTarget}. Progress is in the upload sheet.`, { tone: 'info' });
  }

  // Folder upload: walk, then the dry run (validates depth and folder caps without creating anything), then the
  // pre-flight Modal. Nothing uploads until the user confirms there.
  async function walkAndPreflight(source) {
    const dest = destFolderId;
    setPreflight({ walking: true, count: 0, dest });
    try {
      const entries = source.dataTransfer
        ? await entriesFromDataTransfer(source.dataTransfer, c => setPreflight(p => (p?.walking ? { ...p, count: c } : p)))
        : await entriesFromPicker(source.fileList, c => setPreflight(p => (p?.walking ? { ...p, count: c } : p)));
      if (!entries) {
        setPreflight(null);
        toast('This browser cannot read dropped folders. Use Upload folder instead.', { tone: 'warn' });
        return;
      }
      const summaryNext = buildPreflight(entries);
      if (!summaryNext.fileCount) {
        setPreflight(null);
        toast('That folder contains no uploadable files.', { tone: 'warn' });
        return;
      }
      try {
        const { data } = await api.post(`${BASE}/folders/resolve`, { clientId, parentId: dest || null, paths: summaryNext.folderPaths, dryRun: true });
        setPreflight({ summary: summaryNext, dry: data, error: null, dest });
      } catch (err) {
        setPreflight({ summary: summaryNext, dry: null, error: errMsg(err, 'That folder could not be checked.'), dest });
      }
    } catch (err) {
      setPreflight(null);
      toast(errMsg(err, 'That folder could not be read.'), { tone: 'danger' });
    }
  }

  async function confirmFolderUpload() {
    const { summary: s, dest } = preflight;
    setPreflight(null);
    try {
      // The real resolve: same request as the dry run, so the counts the user approved are the counts that happen.
      const { data } = await api.post(`${BASE}/folders/resolve`, { clientId, parentId: dest || null, paths: s.folderPaths });
      const byPath = new Map(data.resolved.map(r => [r.originalPath.join('/'), r.folderId]));
      s.entries.forEach(e => queueFile(e.file, e.segments.length ? (byPath.get(e.segments.join('/')) || dest || null) : (dest || null)));
      refresh();
      toast(`${plural(s.fileCount, 'file')} uploading. Progress is in the upload sheet.`, { tone: 'info' });
    } catch (err) {
      toast(errMsg(err, 'The folder structure was not created. Try again.'), { tone: 'danger' });
    }
  }

  function onDrop(e) {
    e.preventDefault();
    setDropOver(false);
    if (dropContainsDirectory(e.dataTransfer)) { if (canUploadFolder) walkAndPreflight({ dataTransfer: e.dataTransfer }); }
    else if (canUploadFiles) handleFiles(e.dataTransfer.files);
    else if (canWrite) toast('Pick a folder, or Unfiled, to upload files into.', { tone: 'warn' });
  }
  const dropProps = canUploadFolder ? {
    onDragOver: e => { if ([...(e.dataTransfer?.types || [])].includes('Files')) { e.preventDefault(); setDropOver(true); } },
    onDragLeave: e => { if (!e.currentTarget.contains(e.relatedTarget)) setDropOver(false); },
    onDrop,
  } : {};

  // ── Bulk delete (internal users; per-row permission enforced server-side) ───

  const canBulkDelete = !isExternal;
  const deletableFiles = canBulkDelete ? files.filter(f => mayDeleteFile(deletePerm, f)) : [];
  const toggleFileSelect = id => setSelectedFiles(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  async function handleBulkDelete() {
    const ids = [...selectedFiles];
    if (!ids.length || bulkDeleting) return;
    const ok = await confirm({
      title: `Delete ${plural(ids.length, 'file')} from Storage?`,
      body: deleteBody(retention, true),
      confirmLabel: `Delete ${plural(ids.length, 'file')}`,
      destructive: true,
      typed: ids.length > 1 ? 'delete' : undefined,
    });
    if (!ok) return;
    setBulkDeleting(true);
    let deleted = 0;
    const skipped = [];
    for (const id of ids) {
      try {
        await api.delete(`${BASE}/files/${id}`);   // eslint-disable-line no-await-in-loop
        deleted++;
      } catch (err) {
        const f = files.find(x => x.id === id);
        skipped.push(`${clean(f?.fileName) || id} (${errMsg(err, 'failed')})`);
      }
    }
    setBulkDeleting(false);
    setSelectedFiles(new Set());
    refresh();
    if (skipped.length) {
      toast(`Deleted ${deleted} of ${ids.length}. Skipped ${skipped.length}: ${skipped.slice(0, 3).join(', ')}${skipped.length > 3 ? `, and ${skipped.length - 3} more` : ''}.`,
        { tone: 'warn', duration: 12000 });
    } else {
      toast(`${plural(deleted, 'file')} deleted.`);
    }
  }

  // ── Header ──────────────────────────────────────────────────────────────────

  const totals = summary?.totals || {};
  const clientName = clean(browse?.folder?.clientName)
    || clean(clients.find(c => c.id === clientId)?.name)
    || clean((summary?.clients || []).find(c => c.clientId === clientId)?.clientName) || 'Project';

  usePageHeader({
    title: 'Storage',
    meta: !clientId
      ? (summary ? `${plural(totals.clientsWithContent || 0, 'project')} with content, ${plural(totals.fileCount || 0, 'asset')}, ${formatBytes(totals.totalBytes)}` : 'Loading')
      : `${clientName}. Cold archive: promote a file to Raw footage to transcribe, match or edit it.`,
    actions: (
      <>
        {canMigrate && <Button variant="quiet" icon={ArrowRightLeft} onClick={() => setMigrationOpen(true)}>Frame.io migration</Button>}
        {!isExternal && <Button variant="quiet" icon={Trash2} onClick={() => setDeletedOpen(true)}>Deleted files</Button>}
        {canWrite && (
          <>
            <Button variant="secondary" icon={FolderUp} disabled={!canUploadFolder} onClick={() => folderInput.current?.click()}
              title={!clientId ? 'Select a project first' : selected === 'all' ? 'Creates the folder structure at the project root' : undefined}>
              Upload folder
            </Button>
            <Button variant="primary" icon={Upload} disabled={!canUploadFiles} onClick={() => fileInput.current?.click()}
              title={!clientId ? 'Select a project first' : selected === 'all' ? 'Pick a folder, or Unfiled, to upload files into' : undefined}>
              Upload files
            </Button>
          </>
        )}
      </>
    ),
    filters: (
      <>
        {!isExternal && (
          <Select aria-label="Project" className="ui-fit" value={clientId}
            onChange={e => (e.target.value ? pickClient(e.target.value) : toRollup())}
            options={[{ value: '', label: 'All projects' }, ...clients.map(c => ({ value: c.id, label: clean(c.name) }))]} />
        )}
        <Input aria-label="Search file names" icon={Search} type="search" placeholder="Search file names" value={search} onChange={e => setSearch(e.target.value)} />
        {clientId && (
          <span className="ui-seg" role="group" aria-label="View">
            <Button variant="quiet" icon={List} aria-pressed={viewMode === 'list'} onClick={() => setViewMode('list')}>List</Button>
            <Button variant="quiet" icon={LayoutGrid} aria-pressed={viewMode === 'grid'} onClick={() => setViewMode('grid')}>Gallery</Button>
          </span>
        )}
      </>
    ),
  });

  // ── Folder and file rows ────────────────────────────────────────────────────

  // The Folder column earns its place only when rows can come from more than one folder: "All files", or a search.
  const spansFolders = selected === 'all' || !!search.trim();
  const folderRows = useMemo(() => (browse?.children || []).map(f => ({ ...f, kind: 'folder' })), [browse]);
  const rows = useMemo(() => [...folderRows, ...files.map(f => ({ ...f, kind: 'file' }))], [folderRows, files]);
  const groups = [...(folderRows.length ? [{ key: 'folder', header: 'Folders' }] : []), { key: 'file', header: 'Files' }];
  const cardCount = folderRows.length;
  const cardBytes = folderRows.reduce((a, c) => a + bytesNum(c.totalBytes), 0);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const openRow = r => {
    if (r.kind === 'folder') { openFolder(r.id); setExpanded(prev => new Set(prev).add(r.id)); }
    else setDetail(r);
  };

  // drop (HD-10): where the Grid is narrower than these columns (a phone, or beside the file panel) they leave in this
  // order, Source first; Name always stays. The panel shows every field a dropped column held.
  const columns = [
    ...(canBulkDelete ? [{
      key: 'select', width: 48, drop: 6,
      label: (
        <Checkbox label={<span className="ui-sr">Select every file you may delete</span>}
          checked={deletableFiles.length > 0 && deletableFiles.every(f => selectedFiles.has(f.id))}
          disabled={!deletableFiles.length}
          onChange={e => setSelectedFiles(e.target.checked ? new Set(deletableFiles.map(f => f.id)) : new Set())} />
      ),
      render: r => (r.kind === 'file' && mayDeleteFile(deletePerm, r) ? (
        <span onClick={e => e.stopPropagation()}>
          <Checkbox label={<span className="ui-sr">Select {clean(r.fileName)}</span>} checked={selectedFiles.has(r.id)} onChange={() => toggleFileSelect(r.id)} />
        </span>
      ) : null),
    }] : []),
    { key: 'name', label: 'Name', width: 256, render: r => (r.kind === 'folder' ? (
      <span className="ui-cluster"><Icon icon={Folder} /><span>{clean(r.name)}</span></span>
    ) : (
      <span className="ui-cluster"><span title={clean(r.fileName)}>{clean(r.fileName)}</span><FootageArchiveTag file={r} /></span>
    )) },
    { key: 'size', label: 'Size', width: 96, drop: 7, render: r => <span className="ui-tc">{formatBytes(r.kind === 'folder' ? r.totalBytes : r.fileSizeBytes)}</span> },
    { key: 'source', label: 'Source', width: 128, drop: 1, render: r => (r.kind === 'folder'
      ? <span className="ui-meta">{r.folderCount > 0 ? `${plural(r.folderCount, 'folder')}, ` : ''}{Number(r.fileCount || 0).toLocaleString()} inside</span>
      : sourceLabel(r.source)) },
    { key: 'by', label: 'Uploaded by', width: 116, drop: 2, render: r => (r.kind === 'file' ? (clean(r.uploadedByName) || <span className="ui-meta">Unknown</span>) : null) },
    { key: 'date', label: 'Date', width: 100, drop: 4, render: r => (r.kind === 'file' ? fmtDate(r.createdAt) : null) },
    ...(spansFolders ? [{ key: 'folder', label: 'Folder', width: 112, drop: 3, render: r => (r.kind === 'file' ? (clean(r.folderName) || 'Unfiled') : null) }] : []),
    { key: 'status', label: 'Status', width: 116, drop: 5, render: r => (r.kind === 'file' ? <FileStatus file={r} /> : null) },
  ];
  const gridKey = `hf-grid:storage${canBulkDelete ? '-select' : ''}${spansFolders ? '-folders' : ''}`;

  const emptyFiles = search
    ? { message: 'No file name matches that search. Try a different term, or clear it.', action: { label: 'Clear search', icon: X, onClick: () => setSearch('') } }
    : selected === 'all'
      ? { message: canWrite ? 'Nothing is archived in this project yet. Pick a folder, or Unfiled, then upload files into it.' : 'Nothing has been archived in this project yet.' }
      : selected === 'unfiled'
      ? { message: canWrite ? 'Nothing is unfiled. Files uploaded at the project root land here.' : 'Nothing is unfiled in this project.' }
      : { message: canWrite ? 'This folder is empty. Upload files here to archive them.' : 'Nothing has been archived here yet.' };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="ui ui-page ui-page--flush">
      <input ref={fileInput} type="file" multiple hidden accept={isExternal ? ACCEPT_EXTS : undefined}
        onChange={e => { handleFiles(e.target.files); e.target.value = ''; }} />
      <input ref={folderInput} type="file" multiple hidden webkitdirectory="" directory=""
        onChange={e => { if (e.target.files?.length) walkAndPreflight({ fileList: e.target.files }); e.target.value = ''; }} />

      {!clientId ? (
        <ClientRollup summary={summary} error={summaryError} onRetry={loadSummary} onPick={pickClient} />
      ) : (
        <div className="ui-split">
          <nav className="ui-split__nav" aria-label="Folders">
            <ul className="ui-tree">
              <TreeItem label="All files" count={counts.totalCount} current={selected === 'all'} onSelect={() => openFolder('all')} />
              <TreeItem label="Unfiled" count={counts.unfiledCount} current={selected === 'unfiled'} onSelect={() => openFolder('unfiled')} />
            </ul>
            <div className="ui-tree__sep" role="separator" />
            {tree.length === 0 ? (
              <p className="ui-p ui-dense ui-text-3">No folders yet.{canWrite ? ' Create one to start organising this archive.' : ''}</p>
            ) : (
              <ul className="ui-tree">
                {tree.map(node => (
                  <FolderNode key={node.id} node={node} selectedId={selected} expanded={expanded} onToggle={toggle} onSelect={openFolder}
                    canManage={canWrite}
                    onRename={n => setFolderForm({ mode: 'rename', node: n, name: n.name })}
                    onMove={n => setMovingFolder(n)}
                    onDelete={deleteFolder} />
                ))}
              </ul>
            )}
            {canWrite && (
              <span><Button variant="quiet" icon={FolderPlus} onClick={() => setFolderForm({ mode: 'new', name: '' })}>New folder</Button></span>
            )}
          </nav>

          <div className="ui-split__main ui-droptarget" data-drop={dropOver || undefined} {...dropProps}>
            <div className="ui-inset ui-inset--bar ui-stack ui-stack--tight">
              <nav className="ui-cluster" aria-label="Breadcrumb">
                <Button variant="secondary" iconOnly icon={ArrowUp} label={atRoot ? 'Back to all projects' : 'Up one level'} onClick={() => {
                  const crumbs = browse?.folder?.breadcrumb || [];
                  if (crumbs.length > 1) openFolder(crumbs[crumbs.length - 2].id);
                  else if (crumbs.length === 1) openFolder('all');
                  else toRollup();
                }} />
                {/* Crumbs come from the parent chain's NAMES; `path` is a slug and diverges from the display name. */}
                {atRoot
                  ? <span className="ui-heading" aria-current="page">{clientName}</span>
                  : <Button variant="quiet" onClick={() => openFolder('all')}>{clientName}</Button>}
                {(browse?.folder?.breadcrumb || []).map((c, i, arr) => (
                  <span key={c.id} className="ui-cluster">
                    <Icon icon={ChevronRight} />
                    {i === arr.length - 1
                      ? <span className="ui-heading" aria-current="page">{clean(c.name)}</span>
                      : <Button variant="quiet" onClick={() => openFolder(c.id)}>{clean(c.name)}</Button>}
                  </span>
                ))}
              </nav>
              {/* Two different measures, worded so they cannot be read as one: folder rows report what is inside them all
                  the way down; this line reports what this folder holds directly. */}
              <span className="ui-meta">
                {cardCount > 0 && `${plural(cardCount, 'folder')}, ${formatBytes(cardBytes)} inside. `}
                {selected === 'all'
                  ? `${plural(total, 'asset')} across this project.`
                  : `${plural(browse?.assets?.count ?? 0, 'asset')} held here, ${formatBytes(browse?.assets?.totalBytes)}.`}
              </span>
            </div>

            {viewMode === 'list' && canBulkDelete && selectedFiles.size > 0 && (
              <div className="ui-inset ui-inset--bar ui-cluster">
                <span className="ui-dense">{plural(selectedFiles.size, 'file')} selected</span>
                <Button variant="danger" icon={Trash2} loading={bulkDeleting} onClick={handleBulkDelete}>Delete {plural(selectedFiles.size, 'file')}</Button>
                <Button variant="quiet" icon={X} disabled={bulkDeleting} onClick={() => setSelectedFiles(new Set())}>Clear selection</Button>
              </div>
            )}

            <div className="ui-work">
              {viewMode === 'list' ? (
                <Grid key={gridKey}
                  label={`Storage, ${clientName}`}
                  columns={columns} rows={rows} rowKey={r => r.id}
                  groups={groups} groupOf={r => r.kind}
                  density="medium" storageKey={gridKey} selectedKey={detail?.id} fit
                  onOpen={openRow}
                  state={filesState}
                  empty={emptyFiles}
                  error={{ title: 'The files did not load', message: filesError, onRetry: loadFiles }} />
              ) : (
                <Gallery folders={folderRows} files={files} state={filesState} error={filesError} onRetry={loadFiles}
                  empty={emptyFiles} selectedId={detail?.id} onOpen={openRow} />
              )}
              {detail && (
                <DetailPanel key={detail.id} file={detail} folders={tree} canPromote={canPromote} canWrite={canWrite}
                  deletePerm={deletePerm} retention={retention}
                  onClose={() => { setDetail(null); dismissFinishedDownloads(); }}
                  onChanged={refresh} />
              )}
            </div>

            {totalPages > 1 && (
              <div className="ui-inset ui-inset--bar ui-cluster">
                <Button variant="secondary" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Previous</Button>
                <span className="ui-meta">Page {page} of {totalPages}, {plural(total, 'file')}</span>
                <Button variant="secondary" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next</Button>
              </div>
            )}
          </div>
        </div>
      )}

      <StorageDownloadDock />

      {canMigrate && (
        <FrameioMigrationPanel open={migrationOpen} onClose={() => setMigrationOpen(false)}
          clientId={clientId}
          // 'all' and 'unfiled' are not real folders: a migration into either means the client root (null).
          selectedFolderId={atRoot ? null : selected}
          onMigrated={() => { loadTree(); loadFiles(); }} />
      )}

      {deletedOpen && <DeletedFiles clientId={clientId} onClose={() => setDeletedOpen(false)} onRestored={refresh} />}

      <Modal open={!!folderForm} onClose={() => setFolderForm(null)}
        title={folderForm?.mode === 'rename' ? `Rename ${clean(folderForm?.node?.name)}` : 'New folder'}
        footer={(
          <>
            <Button variant="quiet" onClick={() => setFolderForm(null)}>Cancel</Button>
            <Button variant="primary" disabled={!(folderForm?.name || '').trim()} onClick={submitFolderForm}>
              {folderForm?.mode === 'rename' ? 'Rename folder' : 'Create folder'}
            </Button>
          </>
        )}>
        {folderForm && (
          <form className="ui-stack" onSubmit={e => { e.preventDefault(); submitFolderForm(); }}>
            <Input label="Folder name" value={folderForm.name} data-autofocus autoComplete="off"
              hint={folderForm.mode === 'new' ? `Created in ${atRoot ? 'the project root' : fileTarget}.` : undefined}
              onChange={e => setFolderForm(f => ({ ...f, name: e.target.value }))} />
          </form>
        )}
      </Modal>

      {movingFolder && (
        <MoveFolderModal node={movingFolder} tree={tree} clients={clients} clientId={clientId} isAdmin={isAdmin}
          onClose={() => setMovingFolder(null)}
          onMoved={crossClient => {
            if (selected === movingFolder.id && crossClient) openFolder('all', { replace: true });
            setMovingFolder(null);
            refresh();
          }} />
      )}

      {preflight && (
        <PreflightModal preflight={preflight} destination={preflight.dest ? fileTarget : folderTarget}
          onCancel={() => setPreflight(null)} onConfirm={confirmFolderUpload} />
      )}
    </div>
  );
}

// ── Client rollup: what is in storage across every project the caller can see ──
// Clients with nothing in storage come back with zeros and are folded behind a disclosure.

function ClientRollup({ summary, error, onRetry, onPick }) {
  const [showEmpty, setShowEmpty] = useState(false);
  const rows    = disambiguate(summary?.clients || []);
  const active  = rows.filter(r => r.fileCount > 0 || r.folderCount > 0);
  const empties = rows.filter(r => !(r.fileCount > 0 || r.folderCount > 0));
  // On a phone the counts leave (Last upload first) and the project name stays; a project's own page has the rest.
  const columns = [
    { key: 'project', label: 'Project', width: 320, render: r => r.displayName },
    { key: 'folders', label: 'Folders', width: 110, drop: 2, render: r => Number(r.folderCount || 0).toLocaleString() },
    { key: 'assets', label: 'Assets', width: 110, drop: 3, render: r => Number(r.fileCount || 0).toLocaleString() },
    { key: 'size', label: 'Size', width: 130, drop: 4, render: r => <span className="ui-tc">{formatBytes(r.totalBytes)}</span> },
    { key: 'last', label: 'Last upload', width: 140, drop: 1, render: r => fmtDate(r.lastUploadAt) },
  ];
  return (
    <>
      <div className="ui-work">
        <Grid label="Storage by project" columns={columns} rows={active} rowKey={r => r.clientId}
          density="medium" storageKey="hf-grid:storage-projects" fit onOpen={r => onPick(r.clientId)}
          state={error ? 'error' : summary ? 'data' : 'loading'}
          empty={{ message: 'No project has anything in cold storage yet. Pick a project above to upload into it.' }}
          error={{ title: 'The storage summary did not load', message: 'Check your connection, then try again.', onRetry }} />
      </div>
      {empties.length > 0 && (
        <div className="ui-inset ui-inset--bar ui-stack ui-stack--tight">
          <span>
            <button type="button" className="ui-disclosure ui-dense ui-text-2" aria-expanded={showEmpty} onClick={() => setShowEmpty(v => !v)}>
              <Icon icon={ChevronDown} />{plural(empties.length, 'project')} with nothing in storage
            </button>
          </span>
          {showEmpty && (
            <div className="ui-cluster">
              {empties.map(r => <Button key={r.clientId} variant="secondary" onClick={() => onPick(r.clientId)}>{r.displayName}</Button>)}
            </div>
          )}
        </div>
      )}
    </>
  );
}

// ── Folder tree ───────────────────────────────────────────────────────────────

function TreeItem({ label, count, current, onSelect }) {
  return (
    <li>
      <div className="ui-tree__item" data-current={current || undefined}>
        <span className="ui-tree__twist" aria-hidden="true" />
        <button type="button" className="ui-tree__main" aria-current={current ? 'page' : undefined} onClick={onSelect}>
          <span className="ui-tree__name">{label}</span>
          {count > 0 && <span className="ui-meta">{Number(count).toLocaleString()}</span>}
        </button>
      </div>
    </li>
  );
}

function FolderNode({ node, selectedId, expanded, onToggle, onSelect, onRename, onMove, onDelete, canManage }) {
  const [menu, setMenu] = useState(false);
  const isOpen  = expanded.has(node.id);
  const current = selectedId === node.id;
  const hasKids = node.children && node.children.length > 0;
  const name = clean(node.name);
  return (
    <li>
      <div className="ui-tree__item" data-current={current || undefined}>
        {hasKids ? (
          <button type="button" className="ui-tree__twist" aria-expanded={isOpen} aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${name}`} onClick={() => onToggle(node.id)}>
            <Icon icon={ChevronDown} />
          </button>
        ) : <span className="ui-tree__twist" aria-hidden="true" />}
        <button type="button" className="ui-tree__main" aria-current={current ? 'page' : undefined} onClick={() => onSelect(node.id)}>
          <Icon icon={Folder} />
          <span className="ui-tree__name" title={name}>{name}</span>
          {node.fileCount > 0 && <span className="ui-meta">{Number(node.fileCount).toLocaleString()}</span>}
        </button>
        {canManage && (
          <span className="ui-tree__acts">
            <Popover open={menu} onOpenChange={setMenu} align="end" label={`Folder actions for ${name}`}
              trigger={({ toggle, ref, props }) => (
                <Button ref={ref} variant="quiet" iconOnly icon={MoreHorizontal} label={`Folder actions for ${name}`} onClick={toggle} {...props} />
              )}>
              <Menu label={`Folder actions for ${name}`} onClose={() => setMenu(false)} items={[
                { label: 'Rename', icon: Pencil, onSelect: () => onRename(node) },
                { label: 'Move', icon: FolderInput, onSelect: () => onMove(node) },
                { separator: true },
                { label: 'Delete', icon: Trash2, danger: true, onSelect: () => onDelete(node) },
              ]} />
            </Popover>
          </span>
        )}
      </div>
      {isOpen && hasKids && (
        <ul className="ui-tree ui-tree--nested">
          {node.children.map(child => (
            <FolderNode key={child.id} node={child} selectedId={selectedId} expanded={expanded} onToggle={onToggle} onSelect={onSelect}
              onRename={onRename} onMove={onMove} onDelete={onDelete} canManage={canManage} />
          ))}
        </ul>
      )}
    </li>
  );
}

// ── Gallery view: folders, then files, as tiles ─────────────────────────────────
// footage_archive has no thumbnail column today, so a tile shows the file's extension on the panel ground: never a
// broken image, never a spinner, and never an S3 call (browsing this page must not touch S3).

function Gallery({ folders, files, state, error, onRetry, empty, selectedId, onOpen }) {
  if (state === 'error') return <div className="ui-pane"><ErrorState title="The files did not load" message={error} onRetry={onRetry} /></div>;
  return (
    <div className="ui-pane ui-stack">
      {folders.length > 0 && (
        <section className="ui-section" aria-label="Folders">
          <h2 className="ui-section__title">Folders</h2>
          <div className="ui-tiles">
            {folders.map(f => (
              <button key={f.id} type="button" className="ui-tile" data-key={f.id} onClick={() => onOpen(f)}>
                <span className="ui-tile__thumb"><Icon icon={Folder} size={20} /></span>
                <span className="ui-tile__body">
                  <span className="ui-tile__name" title={clean(f.name)}>{clean(f.name)}</span>
                  <span className="ui-meta">
                    {f.folderCount > 0 ? `${plural(f.folderCount, 'folder')}, ` : ''}{Number(f.fileCount || 0).toLocaleString()} inside, {formatBytes(f.totalBytes)}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </section>
      )}
      <section className="ui-section" aria-label="Files">
        <h2 className="ui-section__title">Files</h2>
        {state === 'loading' ? (
          <div className="ui-tiles">{Array.from({ length: 8 }, (_, i) => <Skeleton key={i} variant="block" height={150} />)}</div>
        ) : files.length === 0 ? (
          <EmptyState {...empty} />
        ) : (
          <div className="ui-tiles">
            {files.map(f => (
              <button key={f.id} type="button" className="ui-tile" data-key={f.id} aria-current={f.id === selectedId || undefined} onClick={() => onOpen({ ...f, kind: 'file' })}>
                <span className="ui-tile__thumb">
                  {f.thumbnailUrl ? <img className="ui-cover" src={f.thumbnailUrl} alt="" /> : extOf(f.fileName)}
                </span>
                <span className="ui-tile__body">
                  <span className="ui-tile__name" title={clean(f.fileName)}>{clean(f.fileName)}</span>
                  <span className="ui-cluster"><span className="ui-meta ui-tc">{formatBytes(f.fileSizeBytes)}</span><FileStatus file={f} link={false} /><FootageArchiveTag file={f} /></span>
                </span>
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// ── Move folder: a new parent within the project, or (Admin, Power User) another project ──

function MoveFolderModal({ node, tree, clients, clientId, isAdmin, onClose, onMoved }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [parentId, setParentId] = useState('');
  const [targetClient, setTargetClient] = useState(clientId || '');
  const [busy, setBusy] = useState(false);
  const crossClient = isAdmin && targetClient && targetClient !== clientId;

  async function submit() {
    if (busy) return;
    if (crossClient) {
      const target = clients.find(c => c.id === targetClient);
      const ok = await confirm({
        title: `Move ${clean(node.name)} to ${clean(target?.name) || 'that project'}?`,
        body: 'The folder and everything inside it move to the other project, at its root.',
        confirmLabel: 'Move folder',
      });
      if (!ok) return;
    }
    setBusy(true);
    try {
      const body = { parentId: parentId || null };
      if (crossClient) body.clientId = targetClient;
      await api.patch(`${BASE}/folders/${node.id}`, body);
      toast(`${clean(node.name)} moved.`);
      onMoved(crossClient);
    } catch (err) {
      toast(errMsg(err, 'The folder was not moved. Try again.'), { tone: 'danger' });
      setBusy(false);
    }
  }

  return (
    <Modal open onClose={() => !busy && onClose()} title={`Move ${clean(node.name)}`}
      footer={(
        <>
          <Button variant="quiet" disabled={busy} onClick={onClose}>Cancel</Button>
          <Button variant="primary" icon={FolderInput} loading={busy} onClick={submit}>Move</Button>
        </>
      )}>
      <div className="ui-stack">
        {isAdmin && (
          <Select label="Project" value={targetClient} onChange={e => { setTargetClient(e.target.value); setParentId(''); }}
            options={clients.map(c => ({ value: c.id, label: clean(c.name) }))} />
        )}
        <Select label="New parent folder" value={parentId} disabled={!!crossClient} onChange={e => setParentId(e.target.value)}
          hint={crossClient ? 'A move to another project lands at that project\'s root.' : undefined}
          options={[{ value: '', label: 'Root (top level)' }, ...indentOptions(flattenTree(tree, 0, node.id))]} />
      </div>
    </Modal>
  );
}

// ── Deleted files: files inside the recovery window, restorable in place ───────

function DeletedFiles({ clientId, onClose, onRestored }) {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [grace, setGrace] = useState(null);
  const [state, setState] = useState('loading');
  const [restoringId, setRestoringId] = useState(null);

  const load = useCallback(async () => {
    setState('loading');
    try {
      const params = clientId ? `?clientId=${clientId}` : '';
      const { data } = await api.get(`${BASE}/files/deleted${params}`);
      setRows(data.rows || []);
      setGrace(data.graceDays ?? null);
    } catch { setRows([]); }
    setState('data');
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  async function restore(row) {
    setRestoringId(row.id);
    try {
      await api.post(`${BASE}/files/${row.id}/restore`);
      await load();
      onRestored();
      toast(`${clean(row.fileName)} restored.`);
    } catch (err) { toast(errMsg(err, 'The file was not restored. Try again.'), { tone: 'danger' }); }
    finally { setRestoringId(null); }
  }

  const columns = [
    { key: 'file', label: 'File', render: r => (
      <span className="ui-stack ui-stack--tight">
        <span>{clean(r.fileName)}</span>
        <span className="ui-meta">{clean(r.clientName) || 'Unassigned'}, {clean(r.folderPath) || 'Unfiled'}, {formatBytes(r.fileSizeBytes)}</span>
      </span>
    ) },
    { key: 'deleted', label: 'Deleted', width: 140, render: r => (
      <span className="ui-stack ui-stack--tight">
        <span>{fmtDate(r.deletedAt) || 'Unknown'}</span>
        {r.deletedByName && <span className="ui-meta">{clean(r.deletedByName)}</span>}
      </span>
    ) },
    { key: 'held', label: 'Recovery', width: 160, render: r => (r.objectRemoved
      ? <Badge tone="danger">Object removed</Badge>
      : r.restorableUntil ? `Held until ${fmtDate(r.restorableUntil)}` : 'Held, no expiry set') },
    { key: 'act', label: <span className="ui-sr">Restore</span>, width: 120, align: 'right', render: r => (r.restorable ? (
      <Button variant="secondary" icon={ArchiveRestore} loading={restoringId === r.id} onClick={() => restore(r)}>Restore</Button>
    ) : null) },
  ];

  return (
    <Modal open onClose={onClose} title="Deleted files">
      <div className="ui-stack">
        <p className="ui-p ui-dense ui-text-2">
          Deleted files{clientId ? ' for this project' : ''}, held in the {grace || 30}-day recovery window. Restore puts a file straight back where it was.
        </p>
        <Table caption="Deleted files" columns={columns} rows={rows} rowKey={r => r.id} state={state} maxHeight="50vh"
          empty={{ message: 'Nothing has been deleted in the recovery window.' }} />
      </div>
    </Modal>
  );
}

// ── Detail panel (was the detail drawer) ──────────────────────────────────────

function DetailPanel({ file, folders, canPromote, canWrite, deletePerm, retention, onClose, onChanged }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [live, setLive]         = useState(file);
  const [notes, setNotes]       = useState(file.notes || '');
  const [saving, setSaving]     = useState(false);
  const [busy, setBusy]         = useState(false);
  const [choosingDest, setChoosingDest] = useState(false);
  const [renaming, setRenaming] = useState(null);   // display name only; the stored object never changes
  const [moveClients, setMoveClients] = useState([]);

  useEffect(() => { setLive(file); setNotes(file.notes || ''); setRenaming(null); }, [file]);
  useEffect(() => {
    if (!deletePerm.isAdmin) return;
    api.get('/api/clients').then(r => setMoveClients(r.data || [])).catch(() => {});
  }, [deletePerm.isAdmin]);

  // While a promote is running, poll the file so the status flips to Promoted without a manual refresh.
  useEffect(() => {
    if (live.promoteStatus !== 'promoting') return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`${BASE}/files/${live.id}`);
        setLive(data);
        if (data.promoteStatus !== 'promoting') onChanged();
      } catch { /* keep polling */ }
    }, 3000);
    return () => clearInterval(t);
  }, [live.promoteStatus, live.id, onChanged]);

  async function patch(body, fallback) {
    const { data } = await api.patch(`${BASE}/files/${live.id}`, body).catch(err => { toast(errMsg(err, fallback), { tone: 'danger' }); throw err; });
    setLive(data);
    onChanged();
    return data;
  }

  async function saveNotes() {
    setSaving(true);
    try { await patch({ notes }, 'The notes did not save. Try again.'); toast('Notes saved.'); } catch { /* toasted */ }
    setSaving(false);
  }

  async function move(folderId) {
    setBusy(true);
    try { await patch({ folderId: folderId || null }, 'The file was not moved. Try again.'); toast('File moved.'); } catch { /* toasted */ }
    setBusy(false);
  }

  async function saveRename() {
    const name = (renaming || '').trim();
    if (!name || name === live.fileName) { setRenaming(null); return; }
    setBusy(true);
    try { await patch({ fileName: name }, 'The file was not renamed. Try again.'); setRenaming(null); } catch { /* toasted */ }
    setBusy(false);
  }

  async function moveToClient(clientId) {
    if (!clientId || clientId === live.clientId) return;
    const target = moveClients.find(c => c.id === clientId);
    const ok = await confirm({
      title: `Move ${clean(live.fileName)} to ${clean(target?.name) || 'that project'}?`,
      body: 'It lands in their Unfiled root and leaves this project\'s storage.',
      confirmLabel: 'Move file',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.patch(`${BASE}/files/${live.id}`, { clientId });
      onChanged();
      onClose();   // the file no longer belongs to the client being browsed
      toast(`${clean(live.fileName)} moved to ${clean(target?.name) || 'the other project'}.`);
    } catch (err) { toast(errMsg(err, 'The file was not moved. Try again.'), { tone: 'danger' }); setBusy(false); }
  }

  async function promote() {
    const ok = await confirm({
      title: `Promote ${clean(live.fileName)} to Raw footage?`,
      body: 'This copies the file into Live Raw footage, where it can be transcribed, matched and processed. The archive copy is kept.',
      confirmLabel: 'Promote',
    });
    if (!ok) return;
    setBusy(true);
    setLive(l => ({ ...l, promoteStatus: 'promoting' }));
    try {
      await api.post(`${BASE}/files/${live.id}/promote`);
      const { data } = await api.get(`${BASE}/files/${live.id}`);
      setLive(data); onChanged();
    } catch (err) {
      toast(errMsg(err, 'The promote failed. Try again.'), { tone: 'danger' });
      try { const { data } = await api.get(`${BASE}/files/${live.id}`); setLive(data); }
      catch { setLive(l => ({ ...l, promoteStatus: 'failed' })); }
    } finally { setBusy(false); }
  }

  async function softDelete() {
    // The window comes from the server (STORAGE_DELETE_GRACE_DAYS), so changing the env cannot make this message lie.
    const ok = await confirm({
      title: `Delete ${clean(live.fileName)} from Storage?`,
      body: deleteBody(retention, false),
      confirmLabel: 'Delete file',
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.delete(`${BASE}/files/${live.id}`);
      onChanged(); onClose();
      toast(`${clean(live.fileName)} deleted.`);
    } catch (err) { toast(errMsg(err, 'The file was not deleted. Try again.'), { tone: 'danger' }); setBusy(false); }
  }

  const flat = flattenTree(folders);
  const canDelete = mayDeleteFile(deletePerm, live);
  const showPromote = canPromote && live.promoteStatus !== 'promoted' && live.promotable;

  return (
    <ExpandedRowPanel open title={clean(live.fileName)} onClose={onClose} label="Archived file"
      footer={(
        <>
          <Button variant={showPromote ? 'secondary' : 'primary'} icon={Download} onClick={() => setChoosingDest(true)}>Download</Button>
          {showPromote && (
            <Button variant="primary" icon={ArrowUp} loading={live.promoteStatus === 'promoting'}
              disabled={busy || live.uploadStatus !== 'complete'} onClick={promote}>
              {live.promoteStatus === 'promoting' ? 'Promoting' : 'Promote to Raw footage'}
            </Button>
          )}
          {canDelete && (
            <Button variant="danger" icon={Trash2} disabled={busy} onClick={softDelete}>
              {retention?.reaperArmed && retention?.graceDays ? `Delete (${retention.graceDays}-day recovery)` : 'Remove from Storage index'}
            </Button>
          )}
        </>
      )}>
      <div className="ui-stack">
        {renaming !== null ? (
          <form className="ui-stack ui-stack--tight" onSubmit={e => { e.preventDefault(); saveRename(); }}>
            <Input label="File name" value={renaming} disabled={busy} autoFocus autoComplete="off" spellCheck={false}
              hint="Changes the display name only; the stored file stays as it is."
              onChange={e => setRenaming(e.target.value)} onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); setRenaming(null); } }} />
            <span className="ui-cluster">
              <Button variant="primary" type="submit" loading={busy}>Save name</Button>
              <Button variant="quiet" disabled={busy} onClick={() => setRenaming(null)}>Cancel</Button>
            </span>
          </form>
        ) : canWrite && (
          <span><Button variant="quiet" icon={Pencil} onClick={() => setRenaming(live.fileName)}>Rename</Button></span>
        )}

        <span className="ui-cluster"><FileStatus file={live} /><FootageArchiveTag file={live} /></span>
        {live.promoteStatus === 'promoted' && live.promotedFootageId && (
          <span><Link className="ui ui-btn ui-btn--quiet" to={`/footage?footage=${live.promotedFootageId}`}>Open in Raw footage</Link></span>
        )}

        <dl className="ui-kv">
          <dt>Folder</dt><dd>{clean(live.folderPath) || 'Unfiled'}</dd>
          <dt>Project</dt><dd>{clean(live.clientName) || 'Unassigned'}</dd>
          <dt>Size</dt><dd className="ui-tc">{formatBytes(live.fileSizeBytes)}</dd>
          <dt>Storage class</dt><dd>{clean(live.storageClass)}</dd>
          <dt>Source</dt><dd>{sourceLabel(live.source)}</dd>
          <dt>Uploaded by</dt><dd>{clean(live.uploadedByName) || 'Unknown'}</dd>
          <dt>Uploaded</dt><dd>{fmtDate(live.createdAt)}</dd>
        </dl>

        <div className="ui-stack ui-stack--tight">
          <Textarea label="Notes" rows={3} value={notes} readOnly={!canWrite} onChange={e => setNotes(e.target.value)} />
          {canWrite && (
            <span><Button variant="secondary" loading={saving} disabled={notes === (live.notes || '')} onClick={saveNotes}>Save notes</Button></span>
          )}
        </div>

        {canWrite && (
          <Select label="Move to folder" value={live.folderId || ''} disabled={busy} onChange={e => move(e.target.value)}
            options={[{ value: '', label: 'Unfiled (project root)' }, ...indentOptions(flat)]} />
        )}

        {deletePerm.isAdmin && moveClients.length > 0 && (
          <Select label="Move to project" value={live.clientId || ''} disabled={busy} onChange={e => moveToClient(e.target.value)}
            hint="Moves this file into another project's storage, at its Unfiled root."
            options={[
              ...(!moveClients.some(c => c.id === live.clientId) ? [{ value: live.clientId || '', label: clean(live.clientName) || 'Unassigned' }] : []),
              ...moveClients.map(c => ({ value: c.id, label: clean(c.name) })),
            ]} />
        )}

        {/* Promote is video-only: it inserts into raw_footage and queues a transcode. Hidden, with the reason said. */}
        {canPromote && !live.promotable && (
          <p className="ui-p ui-meta">Only video files can be promoted to Raw footage. This file stays in the archive and can be downloaded from here.</p>
        )}
        {canDelete && !deletePerm.isAdmin && (
          <p className="ui-p ui-meta">You can remove this one because you uploaded it. Anything uploaded by someone else can only be removed by an Admin.</p>
        )}
      </div>

      {choosingDest && <StorageDownloadModal file={live} api={api} base={BASE} onClose={() => setChoosingDest(false)} />}
    </ExpandedRowPanel>
  );
}
